/* @refresh reset */
import './i18n';

import { StrictMode, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom/client';

import type { ReactNode } from 'react';

import {
  getAuthLoginState,
  LoginState,
  performLogout,
  setLoggedIn,
  useAuthStore
} from '@treneo/features/auth';
import {
  APP_CONFIG_QUERY_KEY,
  createAxiosConfigurator,
  getAppConfigQueryOptions,
  initializeSentry
} from '@treneo/features/config';
import { CURRENT_USER_QUERY_KEY, usersApi } from '@treneo/features/user';

import { AppErrorBoundary } from './bootstrap/app-error-boundary';
import { BootstrapErrorScreen } from './bootstrap/bootstrap-error-screen';
import { BootstrapLoadingScreen } from './bootstrap/bootstrap-loading-screen';
import { Initializer } from './bootstrap/initializer';
import { Providers } from './bootstrap/providers';
import { queryClient } from './bootstrap/query-client';
import { App } from './app';

const BOOTSTRAP_SAFETY_MS = 5000;

const BOOTSTRAP_STATUS = {
  PENDING: 'pending',
  READY: 'ready',
  ERROR: 'error'
} as const;

type BootstrapStatus = (typeof BOOTSTRAP_STATUS)[keyof typeof BOOTSTRAP_STATUS];

const hideHtmlLoader = (): void => {
  const el = document.getElementById('html-loader');
  if (el) {
    el.style.display = 'none';
  }
};

const AppBootstrap = ({ children }: { children: ReactNode }) => {
  const [status, setStatus] = useState<BootstrapStatus>(BOOTSTRAP_STATUS.PENDING);
  const runOnceRef = useRef(false);
  const cancelledRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;
    if (runOnceRef.current) {
      hideHtmlLoader();
      setStatus(BOOTSTRAP_STATUS.READY);
      return;
    }
    runOnceRef.current = true;

    let settled = false;

    const settle = (
      nextStatus: typeof BOOTSTRAP_STATUS.READY | typeof BOOTSTRAP_STATUS.ERROR
    ): void => {
      if (cancelledRef.current || settled) {
        return;
      }
      settled = true;
      hideHtmlLoader();
      setStatus(nextStatus);
    };

    const safetyTimeoutId = setTimeout(() => settle(BOOTSTRAP_STATUS.READY), BOOTSTRAP_SAFETY_MS);

    const getIsLoggedIn = (): boolean => getAuthLoginState() === LoginState.LOGGED;

    void (async () => {
      try {
        await createAxiosConfigurator(() => {
          void performLogout();
        }, getIsLoggedIn);
      } catch {
        settle(BOOTSTRAP_STATUS.ERROR);
        return;
      }

      const [currentUserSettled] = await Promise.allSettled([
        usersApi.getCurrentUser(true),
        queryClient.prefetchQuery(getAppConfigQueryOptions())
      ]);

      const currentUserResult =
        currentUserSettled.status === 'fulfilled' ? currentUserSettled.value : null;
      if (currentUserResult?.isSuccess && currentUserResult.data) {
        setLoggedIn();
        queryClient.setQueryData(CURRENT_USER_QUERY_KEY, currentUserResult.data);
      } else {
        useAuthStore.getState().logoutAction();
      }

      const configData = queryClient.getQueryData<{ sentrySettings?: unknown }>(
        APP_CONFIG_QUERY_KEY
      );
      initializeSentry({ sentrySettings: configData?.sentrySettings ?? {} });

      settle(BOOTSTRAP_STATUS.READY);
    })();

    const onSignalRLogout = (): void => {
      void performLogout();
    };
    window.addEventListener('signalr:logout', onSignalRLogout);

    return () => {
      cancelledRef.current = true;
      clearTimeout(safetyTimeoutId);
      window.removeEventListener('signalr:logout', onSignalRLogout);
    };
  }, []);

  if (status === BOOTSTRAP_STATUS.PENDING) {
    return <BootstrapLoadingScreen />;
  }
  if (status === BOOTSTRAP_STATUS.ERROR) {
    return <BootstrapErrorScreen />;
  }
  return <Initializer>{children}</Initializer>;
};

const rootEl = document.getElementById('root') as HTMLElement;

ReactDOM.createRoot(rootEl).render(
  <StrictMode>
    <AppErrorBoundary>
      <Providers>
        <AppBootstrap>
          <App />
        </AppBootstrap>
      </Providers>
    </AppErrorBoundary>
  </StrictMode>
);
